import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faTimes, faPlus, faEdit, faLanguage, faCheck } from "@fortawesome/free-solid-svg-icons";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import makeAnimated from "react-select/animated";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "../../constant/base_url";

const UpdateBook = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [data, setData] = useState(location.state?.data);

  useEffect(() => {
    if ((!data || !data.pdfs) && id) {
      const fetchBookDetails = async () => {
        console.log("Fetching full book details for ID:", id);
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${baseUrl}/s3/get-book/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          if (response.data?.data) {
            setData(response.data.data);
          }
        } catch (error) {
          console.error("Error fetching book details:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchBookDetails();
    }
  }, [id, data]);
  const animatedComponents = makeAnimated();
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [image, setImage] = useState(null);
  const [showUpdateContentModal, setShowUpdateContentModal] = useState(false);
  const [pageNumber, setPageNumber] = useState("");
  const [pageContent, setPageContent] = useState("");
  const [pageImage, setPageImage] = useState(null);
  const [pageError, setPageError] = useState("");
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [translations, setTranslations] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [availableVersions, setAvailableVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versionError, setVersionError] = useState("");
  const [loadingPage, setLoadingPage] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [savingTranslationIndex, setSavingTranslationIndex] = useState(null);
  const genreOptions = [
    { value: "BedTime", label: "BedTime" },
    { value: "FairyTale", label: "FairyTale" },
    { value: "Adventure", label: "Adventure" },
    { value: "Fantasy", label: "Fantasy" },
    { value: "Magic", label: "Magic" },
    { value: "ScienceFiction", label: "ScienceFiction" },
    { value: "Other", label: "Other" },
  ];
  const formik = useFormik({
    initialValues: {
      book_title: "",
      description: "",
      genre: [],
      age_group: "",
      book_cover_image: "",
      user_id: "",
      organizationName: "",
      storiesPdf: [],
    },
    validationSchema: Yup.object({
      book_title: Yup.string().required("Title is Required"),
      genre: Yup.array()
        .min(1, "At least one genre is required")
        .required("Genre is Required"),
      age_group: Yup.string().required("Age group is Required"),
      user_id: Yup.string().required("User id is Required"),
      organizationName: Yup.string().required("Organization name is Required"),
      storiesPdf: Yup.array()
        .min(1, "At least one pdf fie is required")
        .required("Pdf is Required"),
    }),
  });

  const extractOriginalFilename = (url) => {
    const pattern = /\/([^\/]+)\?/;
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
    return null;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      formik.setFieldValue("book_cover_image", file);
      reader.onload = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  React.useEffect(() => {
    if (data) {
      console.log("Populating form with book data:", data); // Tracing log
      formik.setFieldValue("book_title", data.title);
      formik.setFieldValue("user_id", data.user_id);
      formik.setFieldValue("organizationName", data.organization_name);
      formik.setFieldValue(
        "genre",
        data.genre ? data.genre.split(",").map((item) => ({ label: item, value: item })) : []
      );
      formik.setFieldValue("description", data.description || "");
      formik.setFieldValue("age_group", data.age_group || "");

      const mappedPdfs = data.pdfs?.map((pdf) => {
        const gradeStr = String(pdf.grade);
        const ageGroup =
          gradeStr === "8"
            ? "Grade 8 and below"
            : gradeStr === "4"
              ? "Grade 4 and below"
              : pdf.ageGroup || "";

        return {
          ...pdf,
          ageGroup: ageGroup
        };
      }) || [];

      console.log("Final mapped PDFs for form:", mappedPdfs); // Tracing log
      formik.setFieldValue("storiesPdf", mappedPdfs);
      formik.setFieldValue("book_cover_image", data.book_cover_image || "");
      setImage(data.book_cover_image);
    }
  }, [data]);

  useEffect(() => {
    if (showUpdateContentModal && data?.id) {
      fetchVersions();
    }
  }, [showUpdateContentModal, data]);

  const fetchVersions = async () => {
    setLoadingVersions(true);
    setAvailableVersions([]);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseUrl}/s3/get-book/${data.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data && response.data.data && response.data.data.pdfs) {
        const pdfs = response.data.data.pdfs;
        if (pdfs.length > 0) {
          const versions = pdfs.map((pdf) => ({
            id: pdf.id,
            label: `Grade ${pdf.grade}`,
            value: pdf.id,
            grade: pdf.grade
          }));
          setAvailableVersions(versions);
          setSelectedVersion(versions[0].value); // Default to first version
        } else {
          setVersionError("No versions available for this book.");
        }
      } else {
        setVersionError("Failed to fetch versions.");
      }
    } catch (error) {
      console.error("Error fetching versions:", error);
      setVersionError("Error loading versions. Please try again.");
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleLoadPage = async () => {
    setPageError("");
    if (!pageNumber || pageNumber <= 0) {
      setPageError("Please enter a valid page number greater than 0.");
      return;
    }
    if (!selectedVersion) {
      setPageError("Please select a version.");
      return;
    }

    try {
      // Fetch page content
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseUrl}/s3/book/${data.id}/page`, {
        params: {
          book_lang: "all",
          book_page_no: pageNumber
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.data) {
        const allTranslations = response.data.data;

        // Find selected grade
        const selectedVerObj = availableVersions.find(v => String(v.value) === String(selectedVersion));
        const selectedGrade = selectedVerObj ? selectedVerObj.grade : null;

        // Filter English content for Step 1
        // Try to find exact match with grade, otherwise fallback to any English if grade is null/undefined in response
        const englishContent = allTranslations.find(item =>
          item.book_lang.toLowerCase() === 'english' &&
          (item.grade === selectedGrade || (!item.grade && !selectedGrade))
        );

        // If strict match fails but we have English content and maybe data is inconsistent, 
        // we might want to fallback, but per requirements "show only the grade user has selected".
        // We'll stick to the logic: if we selected a grade, we look for that grade.
        // However, if the API returns grade: null for everything (like the example), we might miss it.
        // Let's add a fallback: if selectedGrade exists but we found nothing, and there's an English entry with null grade, maybe use that?
        // For now, let's assume the API is consistent with the requirements.

        // Actually, let's be a bit more robust:
        let finalEnglishItem = englishContent;
        if (!finalEnglishItem && allTranslations.length > 0) {
          // Fallback logic if needed, or just leave empty
          // finalEnglishItem = allTranslations.find(item => item.book_lang.toLowerCase() === 'english');
        }

        if (finalEnglishItem) {
          setPageContent(finalEnglishItem.translation || "");
          setPageImage(data.book_cover_image || "https://dummyimage.com/600x600/000/fff");
        } else {
          setPageContent("");
          setPageError("No English content found for this version/page.");
          // Still set image if we have it
          setPageImage(data.book_cover_image || "https://dummyimage.com/600x600/000/fff");
        }

        // Populate Translations for Step 2
        const targetLanguages = ["Urdu", "Spanish", "German", "Hindi", "Chinese"];
        const newTranslations = targetLanguages.map(lang => {
          const found = allTranslations.find(item => item.book_lang.toLowerCase() === lang.toLowerCase());
          return {
            language: lang,
            content: found ? found.translation : "No translation found",
            isSaved: !!found // Mark as saved if it came from API
          };
        });
        setTranslations(newTranslations);

      } else {
        setPageContent("");
        setPageError("No content found for this page.");
      }
    } catch (error) {
      console.error("Error fetching page content:", error);
      setPageError("Error loading page content. Please try again.");
    } finally {
      setLoadingPage(false);
    }
  };

  const handlePageImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPageImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const upsertPageContent = async (language, content, book_title, grade = null) => {
    try {
      const payload = {
        book_lang: language,
        book_page_no: pageNumber,
        translation: content,
        grade: grade,
        book_name: book_title,
        profile_id: data.user_id
      };
      const token = localStorage.getItem("token");
      const response = await axios.put(`${baseUrl}/s3/book/${data.id}/page/upsert`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.status === "success") {
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating content:", error);
      return false;
    }
  };

  const handleSavePageContent = async () => {
    if (!pageContent) {
      alert("Page content cannot be empty.");
      return;
    }

    setSavingContent(true);

    // Find selected grade
    const selectedVerObj = availableVersions.find(v => String(v.value) === String(selectedVersion));
    const selectedGrade = selectedVerObj ? selectedVerObj.grade : null;

    const success = await upsertPageContent("english", pageContent, data.title, selectedGrade);

    setSavingContent(false);

    if (success) {
      alert("Page content saved successfully!");
      setShowUpdateContentModal(false);
      setShowTranslationModal(true);
      // Translations are already populated in handleLoadPage
    } else {
      alert("Failed to save page content. Please try again.");
    }
  };

  const handleTranslationChange = (index, value) => {
    const newTranslations = [...translations];
    newTranslations[index].content = value;
    newTranslations[index].isSaved = false; // Reset saved status on change
    setTranslations(newTranslations);
  };

  const handleSaveTranslation = async (index) => {
    setSavingTranslationIndex(index);
    const item = translations[index];

    const success = await upsertPageContent(item.language, item.content, data.title, null);

    setSavingTranslationIndex(null);

    if (success) {
      alert(`${item.language} translation saved successfully!`);
      const newTranslations = [...translations];
      newTranslations[index].isSaved = true;
      setTranslations(newTranslations);
    } else {
      alert(`Failed to save ${item.language} translation.`);
    }
  };

  const handleRetranslate = (index) => {
    // alert(`Re-translating ${translations[index].language}...`);
    // Dummy re-translation logic
    const newTranslations = [...translations];
    newTranslations[index].content = `Re-translated content for ${newTranslations[index].language}`;
    newTranslations[index].isSaved = true; // Assume re-translation is auto-saved or marked as done
    setTranslations(newTranslations);
  };

  const handleSaveAllTranslations = () => {
    alert("All translations saved!");
    setShowTranslationModal(false);
    // Reset everything
    setPageNumber("");
    setPageContent("");
    setPageImage(null);
    setPageError("");
  };

  const handleSubmit = async () => {
    let payload = new FormData();
    const modifiedFiles = formik.values?.storiesPdf
      .filter((item) => item.file)
      ?.map((item) => {
        const dotIndex = item?.file?.name?.lastIndexOf(".");
        const ageSuffix =
          item.ageGroup === "Grade 8 and below"
            ? "8"
            : item.ageGroup === "Grade 4 and below"
              ? "4"
              : "";
        const newFileName =
          item?.file?.name?.substring(0, dotIndex) +
          `_${item.language}_${ageSuffix}` +
          item?.file?.name?.substring(dotIndex);
        console.log("New file name is: " + newFileName);
        return new File([item?.file], newFileName, { type: item?.file?.type });
      });
    payload.append("book_title", formik.values.book_title);
    payload.append(
      "genre",
      formik.values.genre.map((item) => item.label)?.join(", ")
    );
    if (!!formik.values?.removeUrl?.length) {
      formik.values?.removeUrl?.forEach((item) => payload.append("removeUrl", item))
    }

    payload.append("age_group", formik.values.age_group);
    if (formik.values?.book_cover_image instanceof File) {
      payload.append("book_cover_image", formik.values.book_cover_image);
    }
    payload.append("user_id", data?.user_id || "12345556");
    payload.append("organizationName", data?.organization_name || "FictionGenie");
    payload.append("description", formik.values.description);
    modifiedFiles?.forEach((item) => payload.append("storiesPdf", item));
    setLoading(true);
    const token = localStorage.getItem("token");
    axios
      .put(`${baseUrl}/s3/book/${data?.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((res) => {
        setLoading(false);
        navigate("/books");
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const handleViewBook = () => {
    setShowSuccessModal(false);
  };

  const validForSubmit = false;// !formik.values.storiesPdf.some(item => item?.pdfPath) && !!(formik.values.storiesPdf.findIndex(item => item?.language && item?.file) === -1)

  return (
    <div className="custom-container" >
      <section className="py-5 content">
        <Container className="px-4 px-lg-5 my-5">
          <Row className="gx-4 gx-lg-5 align-items-start">
            <Col md={6}>
              <div className="position-relative">
                <div
                  className="card-img-top mb-5 mb-md-0"
                  onClick={() => document.getElementById("fileInput").click()}
                  style={{
                    backgroundImage: `url(${image ||
                      "https://dummyimage.com/600x400/dee2e6/6c757d.jpg"
                      })`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    width: "100%",
                    paddingTop: "100%", // Aspect ratio 1:1 (width:height)
                    borderRadius: "10px",
                    cursor: "pointer", // Change cursor to pointer
                    border: "5px solid black", // Add black border
                  }}
                />
                {!image && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      borderRadius: "10px",
                      pointerEvents: "none", // Make overlay transparent to clicks
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faUpload}
                      style={{ color: "white", fontSize: "2rem" }}
                    />
                    <h5 className="text-white mt-3">Select Book Cover</h5>
                  </div>
                )}
                <input
                  id="fileInput"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />
              </div>
              {/* {errors.image && <div className="text-danger">{errors.image}</div>} */}
            </Col>
            <Col md={6}>
              <h1 className="display-6 fw-bolder">Update Book Details</h1>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Title <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter title"
                    id="book_title"
                    name="book_title"
                    value={formik.values.book_title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.touched.book_title && !!formik.errors.book_title
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.book_title}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Genre <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Select
                      isMulti={true}
                      closeMenuOnSelect={false}
                      components={animatedComponents}
                      id="genre"
                      name="genre"
                      classNamePrefix="select"
                      options={genreOptions}
                      className={
                        !!formik.touched.genre &&
                        !!formik.errors.genre &&
                        "is-invalid"
                      }
                      value={formik.values?.genre || []}
                      onChange={(data) => formik.setFieldValue("genre", data)}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.touched.genre && !!formik.errors.genre
                      }
                    />
                  </InputGroup>
                  {!!formik.touched.genre && !!formik.errors.genre && (
                    <div className="text-danger">{formik.errors.genre}</div>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Age Group <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      as="select"
                      name="age_group"
                      id="age_group"
                      value={formik.values.age_group}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.touched.age_group && !!formik.errors.age_group
                      }
                    >
                      <option value="">Select age group</option>
                      <option value="Less than 6 years">
                        Less than 6 years
                      </option>
                      <option value="6-8 years">6-8 years</option>
                      <option value="8-10 years">8-10 years</option>
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.age_group}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group className="mb-4">
                  <Form.Label>
                    Upload PDFs <span className="text-danger">*</span>
                  </Form.Label>
                  {formik.values?.storiesPdf?.map((pdf, index) => (
                    <div key={index} className="mb-4 d-flex align-items-center">
                      <Form.Group className="flex-grow-1 me-2">
                        <Form.Control
                          as="select"
                          value={
                            formik.values.storiesPdf?.[
                              index
                            ]?.language?.includes("_")
                              ? formik.values.storiesPdf?.[
                                index
                              ]?.language?.split("_")?.[1]
                              : formik.values.storiesPdf[index]?.language
                          }
                          onChange={(e) => {
                            const selectedAgeGroup = formik.values.storiesPdf[index]?.ageGroup;
                            if (
                              formik.values?.storiesPdf?.findIndex(
                                (item) =>
                                  item?.language?.toLowerCase() === e.target.value.toLowerCase() &&
                                  item?.ageGroup === selectedAgeGroup
                              ) !== -1
                            ) {
                              alert("Pdf with the same Language and Age Group is already added");
                              return;
                            }
                            formik.setFieldValue(`storiesPdf[${index}]`, {
                              ...formik.values.storiesPdf[index],
                              language: e.target.value,
                            });
                          }}
                        >
                          <option value="">Select Language</option>
                          <option value="English">English</option>
                          <option value="Chinese">Chinese</option>
                          <option value="Hindi">Hindi</option>
                          <option value="Urdu">Urdu</option>
                          <option value="German">German</option>
                          <option value="French">French</option>
                        </Form.Control>
                      </Form.Group>
                      <Form.Group className="flex-grow-1 me-2">
                        <Form.Control
                          as="select"
                          value={
                            formik.values.storiesPdf?.[
                              index
                            ]?.ageGroup || ""
                          }
                          onChange={(e) => {
                            const selectedLanguage = formik.values.storiesPdf[index]?.language;
                            if (
                              formik.values?.storiesPdf?.findIndex(
                                (item) =>
                                  item?.ageGroup === e.target.value &&
                                  item?.language?.toLowerCase() === selectedLanguage?.toLowerCase()
                              ) !== -1
                            ) {
                              alert("Pdf with the same Language and Age Group is already added");
                              return;
                            }
                            formik.setFieldValue(`storiesPdf[${index}]`, {
                              ...formik.values.storiesPdf[index],
                              ageGroup: e.target.value,
                            });
                          }}
                        >
                          <option value="">Age</option>
                          <option value="Grade 8 and below">Grade 8 and below</option>
                          <option value="Grade 4 and below">Grade 4 and below</option>
                        </Form.Control>
                      </Form.Group>
                      <Form.Group
                        className={
                          formik.values?.storiesPdf?.[index]?.pdfPath?.includes(
                            "https://fiction-genie"
                          )
                            ? "input-upload me-2"
                            : "position-relative me-2"
                        }
                      >
                        {formik.values?.storiesPdf?.[index]?.pdfPath?.includes(
                          "https://fiction-genie"
                        ) ? (
                          <span>
                            {extractOriginalFilename(
                              formik.values.storiesPdf[index].pdfPath
                            )}
                          </span>
                        ) : (
                          <Form.Control
                            type="file"
                            accept=".pdf"
                            onChange={(e) => {
                              if (
                                formik.values.storiesPdf?.filter(
                                  (item) =>
                                    item?.language?.toLowerCase() ===
                                    formik.values?.storiesPdf?.[index]?.language?.toLowerCase() &&
                                    item?.ageGroup === formik.values.storiesPdf[index]?.ageGroup
                                )?.length > 1
                              ) {
                                alert("Pdf with the same Language and Age Group is already added");
                                return;
                              }
                              const selectedAgeGroup = formik.values.storiesPdf[index]?.ageGroup;
                              const ageSuffix =
                                selectedAgeGroup === "Grade 8 and below"
                                  ? "8"
                                  : selectedAgeGroup === "Grade 4 and below"
                                    ? "4"
                                    : "";
                              formik.setFieldValue(`storiesPdf[${index}]`, {
                                ...formik.values.storiesPdf[index],
                                file: e.target.files[0],
                                fileName: `${e.target.files[0]?.name}_${ageSuffix}`,
                              });
                            }}
                          />
                        )}
                      </Form.Group>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`tooltip-remove-${index}`}>
                            Remove
                          </Tooltip>
                        }
                      >
                        <Button
                          variant="danger"
                          className="ms-1"
                          onClick={() => {
                            if (formik.values?.storiesPdf.length === 1) {
                              alert('Atleast 1 pdf should be there. ')
                              return
                            }
                            if (formik.values?.storiesPdf?.[index]?.book_id) {
                              const fileName = new URL(formik.values?.storiesPdf?.[index]?.pdfPath).pathname.replaceAll("%20", " ") || ''
                              const temp = [...(formik.values?.removeUrl || [])]?.concat(fileName)
                              formik.setFieldValue("removeUrl", temp);
                            }
                            const temp = [...formik.values.storiesPdf];
                            temp.splice(index, 1);
                            formik.setFieldValue("storiesPdf", temp);
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-add`}>Add More</Tooltip>}
                      >
                        <Button
                          variant="primary"
                          className="ms-1"
                          onClick={() =>
                            formik.setFieldValue("storiesPdf", [
                              ...formik.values.storiesPdf,
                              { language: "", ageGroup: "", file: null },
                            ])
                          }
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  ))}
                  {!!formik.touched.storiesPdf &&
                    !!formik.errors.storiesPdf && (
                      <div className="text-danger">
                        {formik.errors.storiesPdf}
                      </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Book Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter book description (Optional)"
                    name="description"
                    id="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </Form.Group>
                <Button
                  variant="success"
                  onClick={handleSubmit}
                  className="mt-3 btn-width"
                  disabled={validForSubmit || loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Upload my story :)"
                  )}
                </Button>
                <Button
                  variant="outline-primary"
                  className="mt-3 ms-2 d-inline-flex align-items-center"
                  onClick={() => setShowUpdateContentModal(true)}
                  style={{ borderRadius: "20px", padding: "0.375rem 1rem" }}
                >
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Update Page Content
                </Button>
              </Form>

            </Col>
          </Row>
        </Container>
      </section>
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Book Added Successfully :)</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleViewBook}>
            View Book
          </Button>
          <Button variant="secondary" onClick={() => { }}>
            Add Another
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showUpdateContentModal} onHide={() => setShowUpdateContentModal(false)} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold text-primary">
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Step 1: Update Page Content
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form.Group className="mb-4">
            <div className="d-flex gap-3">
              <div style={{ flex: 1 }}>
                <Form.Label className="fw-bold">Version</Form.Label>
                {loadingVersions ? (
                  <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading...</span>
                  </div>
                ) : versionError ? (
                  <div className="text-danger small">{versionError}</div>
                ) : (
                  <Form.Select
                    value={selectedVersion}
                    onChange={(e) => setSelectedVersion(e.target.value)}
                    disabled={availableVersions.length === 0}
                  >
                    {availableVersions.map((ver) => (
                      <option key={ver.value} value={ver.value}>
                        {ver.label}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </div>
              <div style={{ flex: 1, maxWidth: "200px" }}>
                <Form.Label className="fw-bold">Page Number</Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    type="number"
                    placeholder="#"
                    value={pageNumber}
                    onChange={(e) => {
                      setPageNumber(e.target.value);
                      setPageError("");
                    }}
                    isInvalid={!!pageError}
                  />
                  <Button variant="primary" onClick={handleLoadPage} disabled={loadingPage}>
                    {loadingPage ? <Spinner animation="border" size="sm" /> : "Load"}
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {pageError}
                  </Form.Control.Feedback>
                </InputGroup>
              </div>
            </div>
          </Form.Group>
          {pageContent && (
            <div className="border rounded p-3 bg-light">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Page Content</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={8}
                      value={pageContent}
                      onChange={(e) => setPageContent(e.target.value)}
                      style={{ resize: "none" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Page Image</Form.Label>
                    <div
                      className="position-relative border rounded"
                      style={{
                        width: "100%",
                        paddingTop: "100%", // 1:1 Aspect Ratio
                        backgroundImage: `url(${pageImage || "https://dummyimage.com/600x600/dee2e6/6c757d.jpg"})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        cursor: "pointer"
                      }}
                      onClick={() => document.getElementById("pageImageInput").click()}
                    >
                      {!pageImage && (
                        <div className="position-absolute top-50 start-50 translate-middle text-center">
                          <FontAwesomeIcon icon={faUpload} size="2x" className="text-secondary" />
                          <div className="text-secondary mt-2">Upload Image</div>
                        </div>
                      )}
                    </div>
                    <input
                      id="pageImageInput"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handlePageImageChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="success" onClick={handleSavePageContent} disabled={!pageContent || savingContent}>
            {savingContent ? <Spinner animation="border" size="sm" /> : "Save Page"}
          </Button>
          <Button variant="secondary" onClick={() => setShowUpdateContentModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showTranslationModal} onHide={() => setShowTranslationModal(false)} size="xl" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold text-primary">
            <FontAwesomeIcon icon={faLanguage} className="me-2" />
            Step 2: Update Translations
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Desktop View */}
          <Table striped bordered hover responsive className="d-none d-md-table">
            <thead>
              <tr>
                <th>Language</th>
                <th>Content</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {translations.map((item, index) => (
                <tr key={index} className={item.isSaved ? "table-success" : ""}>
                  <td style={{ width: "15%" }}>
                    {item.language}
                    {item.isSaved && <FontAwesomeIcon icon={faCheck} className="ms-2 text-success" />}
                  </td>
                  <td>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={item.content}
                      onChange={(e) => handleTranslationChange(index, e.target.value)}
                    />
                  </td>
                  <td style={{ width: "25%" }}>
                    <div className="d-flex gap-2 align-items-center">
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => handleRetranslate(index)}
                      >
                        Re-translate
                      </Button>
                      <Button
                        variant={item.isSaved ? "success" : "outline-success"}
                        size="sm"
                        onClick={() => handleSaveTranslation(index)}
                        disabled={savingTranslationIndex === index}
                      >
                        {savingTranslationIndex === index ? (
                          <Spinner animation="border" size="sm" />
                        ) : item.isSaved ? (
                          "Saved"
                        ) : (
                          "Save"
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Mobile View */}
          <div className="d-md-none">
            {translations.map((item, index) => (
              <div key={index} className={`card mb-3 ${item.isSaved ? "border-success" : ""}`}>
                <div className={`card-header d-flex justify-content-between align-items-center ${item.isSaved ? "bg-success text-white" : "bg-light"}`}>
                  <span className="fw-bold">{item.language}</span>
                  {item.isSaved && <FontAwesomeIcon icon={faCheck} />}
                </div>
                <div className="card-body">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={item.content}
                    onChange={(e) => handleTranslationChange(index, e.target.value)}
                    className="mb-3"
                  />
                  <div className="d-flex justify-content-between">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => handleRetranslate(index)}
                    >
                      Re-translate
                    </Button>
                    <Button
                      variant={item.isSaved ? "success" : "outline-success"}
                      size="sm"
                      onClick={() => handleSaveTranslation(index)}
                      disabled={savingTranslationIndex === index}
                    >
                      {savingTranslationIndex === index ? (
                        <Spinner animation="border" size="sm" />
                      ) : item.isSaved ? (
                        "Saved"
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowTranslationModal(false);
            setShowUpdateContentModal(true);
          }}>
            Back
          </Button>
          {/* <Button variant="primary" onClick={handleSaveAllTranslations}>
            Save All
          </Button> */}
          <Button variant="secondary" onClick={() => setShowTranslationModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <footer className="py-2 bg-dark custom-footer">
        <Container>
          <p className="m-0 text-center text-white">
            Copyright &copy; FictionGenie 2025
          </p>
        </Container>
      </footer>
    </div >
  );
};

export default UpdateBook;
